import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ChevronUp, ChevronDown, GripVertical, Sparkles, ChevronRight, X, Search, Check, Flame, Sun, Moon, Clock, Minus, Plus, ChevronsUpDown } from 'lucide-react';
import { API, authFetch } from '@/App';
import { toast } from 'sonner';

// ──────────────────────────────────────────
// ROUTINE TEMPLATES
// ──────────────────────────────────────────
const ROUTINE_TEMPLATES = [
  {
    id: 'oily-beginner', name: 'Oily Skin – Beginner', tag: 'Oily Skin',
    color: 'from-emerald-400 to-teal-500', category: 'skincare',
    steps: [
      { label: 'Face Wash', hint: 'gel-based', types: ['cleanser'] },
      { label: 'Niacinamide Serum', hint: 'oil control', types: ['serum'] },
      { label: 'Oil-free Moisturiser', hint: 'lightweight', types: ['moisturizer'] },
      { label: 'Sunscreen', hint: 'matte finish', types: ['sunscreen'] },
    ],
  },
  {
    id: 'acne-routine', name: 'Acne Routine', tag: 'Acne',
    color: 'from-rose-400 to-red-500', category: 'skincare',
    steps: [
      { label: 'Gentle Cleanser', hint: 'low pH', types: ['cleanser'] },
      { label: 'Salicylic Acid', hint: 'BHA exfoliant', types: ['serum', 'exfoliator'] },
      { label: 'Spot Treatment', hint: 'targeted care', types: ['spot_treatment', 'serum'] },
      { label: 'Moisturiser', hint: 'non-comedogenic', types: ['moisturizer'] },
      { label: 'Sunscreen', hint: 'SPF 50+', types: ['sunscreen'] },
    ],
  },
  {
    id: 'dry-skin', name: 'Dry Skin Routine', tag: 'Dry Skin',
    color: 'from-sky-400 to-blue-500', category: 'skincare',
    steps: [
      { label: 'Hydrating Cleanser', hint: 'cream-based', types: ['cleanser'] },
      { label: 'Hyaluronic Acid', hint: 'hydration booster', types: ['serum'] },
      { label: 'Rich Moisturiser', hint: 'cream texture', types: ['moisturizer'] },
      { label: 'Sunscreen', hint: 'hydrating formula', types: ['sunscreen'] },
    ],
  },
  {
    id: 'glow-routine', name: 'Glow Routine', tag: 'Glow',
    color: 'from-amber-400 to-orange-500', category: 'skincare',
    steps: [
      { label: 'Cleanser', hint: 'brightening', types: ['cleanser'] },
      { label: 'Vitamin C Serum', hint: 'antioxidant power', types: ['serum'] },
      { label: 'Moisturiser', hint: 'glow-boosting', types: ['moisturizer'] },
      { label: 'Sunscreen', hint: 'dewy finish', types: ['sunscreen'] },
    ],
  },
  {
    id: 'wedding-guest', name: 'Wedding Guest Look', tag: 'Spotlight',
    color: 'from-pink-400 to-fuchsia-500', category: 'makeup',
    steps: [
      { label: 'Primer', hint: 'pore-blurring', types: ['primer'] },
      { label: 'Foundation', hint: 'long-wear', types: ['foundation', 'bb_cream'] },
      { label: 'Concealer', hint: 'brightening', types: ['concealer'] },
      { label: 'Blush', hint: 'rosy glow', types: ['blush'] },
      { label: 'Lipstick', hint: 'statement shade', types: ['lipstick', 'lip_gloss'] },
      { label: 'Setting Spray', hint: 'all-day hold', types: ['setting_spray'] },
    ],
  },
  {
    id: 'night-routine', name: 'Night Routine', tag: 'PM Care',
    color: 'from-indigo-400 to-purple-500', category: 'skincare',
    steps: [
      { label: 'Makeup Remover', hint: 'oil or micellar', types: ['cleanser'] },
      { label: 'Face Wash', hint: 'deep clean', types: ['cleanser'] },
      { label: 'Serum', hint: 'retinol or peptide', types: ['serum'] },
      { label: 'Eye Cream', hint: 'anti-dark circle', types: ['eye_cream'] },
      { label: 'Night Cream', hint: 'repair & nourish', types: ['moisturizer'] },
    ],
  },
  {
    id: 'morning-routine', name: 'Morning Routine', tag: 'AM Care',
    color: 'from-yellow-400 to-amber-500', category: 'skincare',
    steps: [
      { label: 'Gentle Cleanser', hint: 'mild wash', types: ['cleanser'] },
      { label: 'Toner', hint: 'prep & hydrate', types: ['toner'] },
      { label: 'Moisturiser', hint: 'day cream', types: ['moisturizer'] },
      { label: 'Sunscreen', hint: 'SPF 50+', types: ['sunscreen'] },
    ],
  },
  {
    id: 'hydration-boost', name: 'Hydration Boost', tag: 'Hydration',
    color: 'from-cyan-400 to-sky-500', category: 'skincare',
    steps: [
      { label: 'Hydrating Cleanser', hint: 'gentle', types: ['cleanser'] },
      { label: 'Hydrating Toner', hint: 'layer moisture', types: ['toner'] },
      { label: 'Hyaluronic Serum', hint: 'moisture magnet', types: ['serum'] },
      { label: 'Rich Moisturiser', hint: 'lock it in', types: ['moisturizer'] },
    ],
  },
  {
    id: 'self-care-sunday', name: 'Self-Care Sunday', tag: 'Self-Care',
    color: 'from-violet-400 to-purple-500', category: 'skincare',
    steps: [
      { label: 'Oil Cleanser', hint: 'melt impurities', types: ['cleanser'] },
      { label: 'Exfoliator', hint: 'AHA/BHA', types: ['exfoliator'] },
      { label: 'Face Mask', hint: 'clay or sheet', types: ['mask'] },
      { label: 'Serum', hint: 'treatment boost', types: ['serum'] },
      { label: 'Moisturiser', hint: 'pamper skin', types: ['moisturizer'] },
    ],
  },
];

// ──────────────────────────────────────────
// FULL-SCREEN PRODUCT PICKER (mobile-first)
// ──────────────────────────────────────────
function ProductPicker({ open, onClose, onSelect, step, category, currentProductId }) {
  const [search, setSearch] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setSearch('');
      setSelectedBrand('');
      fetchProducts('', '');
      fetchBrands();
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [open]);

  const fetchBrands = async () => {
    try {
      const res = await authFetch(`${API}/products/brands-for-category?category=${category}`);
      if (res.ok) {
        const data = await res.json();
        setBrands(data.slice(0, 50));
      }
    } catch (e) { console.error(e); }
  };

  const fetchProducts = async (query, brand) => {
    setLoading(true);
    try {
      const allProducts = [];
      const seenIds = new Set();

      for (const type of (step?.types || [])) {
        const params = new URLSearchParams({ category, product_type: type, limit: '40' });
        if (query) params.append('search', query);
        if (brand) params.append('brand', brand);
        const res = await authFetch(`${API}/products?${params}`);
        if (res.ok) {
          const data = await res.json();
          data.forEach(p => {
            if (!seenIds.has(p.product_id)) {
              seenIds.add(p.product_id);
              allProducts.push(p);
            }
          });
        }
      }
      setProducts(allProducts);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (val) => {
    setSearch(val);
    fetchProducts(val, selectedBrand);
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    fetchProducts(search, brand);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-background flex flex-col" data-testid="product-picker">
      {/* Sticky header with search */}
      <div className="shrink-0 bg-background border-b border-border safe-top">
        <div className="flex items-center gap-2 px-3 py-3">
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full shrink-0"
            data-testid="picker-close-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {step?.label}
            </p>
            <p className="text-[11px] text-muted-foreground">{step?.hint}</p>
          </div>
        </div>
        <div className="px-3 pb-3">
          <div className="relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              ref={inputRef}
              type="text"
              data-testid="picker-search"
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={`Search ${step?.label?.toLowerCase() || 'products'}...`}
              className="w-full pl-9 pr-9 py-2.5 rounded-xl border border-border bg-gray-50 focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all outline-none text-sm"
            />
            {search && (
              <button onClick={() => handleSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
          {/* Brand filter dropdown */}
          {brands.length > 0 && (
            <div className="mt-2 relative" data-testid="brand-filter-dropdown">
              <select
                value={selectedBrand}
                onChange={(e) => handleBrandSelect(e.target.value)}
                data-testid="brand-select"
                className="w-full px-3 py-2.5 rounded-xl border border-border bg-gray-50 focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all outline-none text-sm appearance-none cursor-pointer"
              >
                <option value="">All Brands ({brands.length})</option>
                {brands.map(b => (
                  <option key={b.brand} value={b.brand}>{b.brand} ({b.count})</option>
                ))}
              </select>
              <ChevronsUpDown className="w-4 h-4 text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          )}
        </div>
      </div>

      {/* Product list — takes remaining height, scrollable */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 px-6 text-muted-foreground">
            <p className="text-sm">No products found</p>
            <p className="text-xs mt-1">Try a different search term</p>
          </div>
        ) : (
          <div className="divide-y divide-border/50">
            {products.map(product => {
              const isSelected = product.product_id === currentProductId;
              return (
                <button
                  key={product.product_id}
                  data-testid={`picker-product-${product.product_id}`}
                  onClick={() => { onSelect(product); onClose(); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors active:bg-gray-100 ${
                    isSelected ? 'bg-primary/5' : ''
                  }`}
                >
                  {product.image ? (
                    <img
                      src={product.image} alt=""
                      className="w-11 h-11 rounded-lg object-cover shrink-0"
                      onError={(e) => { e.target.style.display = 'none'; if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = 'flex'; }}
                    />
                  ) : null}
                  <div
                    className="w-11 h-11 rounded-lg bg-gradient-to-br from-pink-400 to-orange-300 items-center justify-center text-white font-bold shrink-0 text-sm"
                    style={{ display: product.image ? 'none' : 'flex', fontFamily: 'Outfit, sans-serif' }}
                  >g</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm leading-tight line-clamp-2">{product.name}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{product.brand}</p>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="text-sm font-bold text-primary">{product.price}</p>
                    {isSelected && <Check className="w-4 h-4 text-primary ml-auto mt-0.5" />}
                  </div>
                </button>
              );
            })}
          </div>
        )}
        {/* Bottom padding for safe area */}
        <div className="h-8" />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────
// PRODUCT TYPE DISPLAY NAMES
// ──────────────────────────────────────────
const typeNames = {
  'cleanser': 'Cleansers', 'toner': 'Toners', 'serum': 'Serums',
  'moisturizer': 'Moisturizers', 'sunscreen': 'Sunscreens', 'exfoliator': 'Exfoliators',
  'mask': 'Face Masks', 'eye_cream': 'Eye Creams', 'lip_care': 'Lip Care',
  'spot_treatment': 'Spot Treatments', 'body_wash': 'Body Wash', 'body_lotion': 'Body Lotions',
  'lipstick': 'Lipsticks', 'lip_gloss': 'Lip Gloss', 'lip_liner': 'Lip Liners',
  'foundation': 'Foundations', 'concealer': 'Concealers', 'primer': 'Primers',
  'blush': 'Blush', 'highlighter': 'Highlighters', 'bronzer': 'Bronzers',
  'eyeshadow': 'Eyeshadows', 'eyeliner': 'Eyeliners', 'kajal': 'Kajal',
  'mascara': 'Mascaras', 'compact': 'Compact Powder', 'setting_spray': 'Setting Sprays',
  'nail_polish': 'Nail Polish', 'brow': 'Brow Products',
  'shampoo': 'Shampoos', 'conditioner': 'Conditioners', 'hair_oil': 'Hair Oils',
  'hair_serum': 'Hair Serums', 'hair_mask': 'Hair Masks', 'hair_color': 'Hair Color',
  'dry_shampoo': 'Dry Shampoos', 'hand_cream': 'Hand Creams',
  'perfume': 'Perfumes', 'deodorant': 'Deodorants', 'oil': 'Face Oils',
};
// Fallback: convert snake_case to Title Case
const formatTypeName = (t) => typeNames[t] || t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// ──────────────────────────────────────────
// MAIN PAGE
// ──────────────────────────────────────────
export default function CreateChecklistPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedProductId = location.state?.preselectedProduct || null;
  const brandDropdownRef = useRef(null);

  // Tab: 'scratch' | 'routine'
  const [tab, setTab] = useState('scratch');

  // -- Common state --
  const [name, setName] = useState('');
  const [category, setCategory] = useState('skincare');
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [checklistMax, setChecklistMax] = useState(5);

  // -- Scratch mode --
  const [productType, setProductType] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [availableProducts, setAvailableProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [brandsList, setBrandsList] = useState([]);
  const [typesList, setTypesList] = useState([]);
  const [brandSearch, setBrandSearch] = useState('');
  const [showBrandDropdown, setShowBrandDropdown] = useState(false);
  const [searchedBrands, setSearchedBrands] = useState([]);

  // -- Routine mode --
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [templateSlots, setTemplateSlots] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerStepIndex, setPickerStepIndex] = useState(null);

  // -- Post-create --
  const [showRoutinePrompt, setShowRoutinePrompt] = useState(false);
  const [createdChecklistId, setCreatedChecklistId] = useState(null);

  useEffect(() => {
    fetchUser();
    fetchLimits();
    const handleClickOutside = (e) => {
      if (brandDropdownRef.current && !brandDropdownRef.current.contains(e.target)) setShowBrandDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (tab === 'scratch') { fetchProducts(); fetchBrandsAndTypes(); }
  }, [category, tab]);

  useEffect(() => {
    if (tab === 'scratch') fetchProducts();
  }, [brandFilter, productType, priceRange]);

  // Pre-select product from Shop page navigation
  useEffect(() => {
    if (preselectedProductId) {
      setTab('scratch');
      setSelectedProducts(prev => prev.includes(preselectedProductId) ? prev : [...prev, preselectedProductId]);
    }
  }, [preselectedProductId]);

  const fetchUser = async () => {
    try { const r = await authFetch(`${API}/auth/me`); if (r.ok) setUser(await r.json()); } catch (e) { /* */ }
  };
  const fetchLimits = async () => {
    try { const r = await authFetch(`${API}/checklists/limits`); if (r.ok) { const d = await r.json(); setChecklistMax(d.max); } } catch (e) { /* */ }
  };
  const fetchBrandsAndTypes = async () => {
    try {
      const [bRes, tRes] = await Promise.all([
        authFetch(`${API}/products/brands-for-category?category=${category}`),
        authFetch(`${API}/products/types?category=${category}`)
      ]);
      if (bRes.ok) setBrandsList(await bRes.json());
      if (tRes.ok) setTypesList(await tRes.json());
    } catch (e) { /* */ }
  };
  const handleBrandSearch = async (q) => {
    setBrandSearch(q);
    if (q.length >= 1) {
      try { const r = await authFetch(`${API}/products/brands-for-category?category=${category}&search=${encodeURIComponent(q)}`); if (r.ok) setSearchedBrands(await r.json()); } catch (e) { /* */ }
    } else { setSearchedBrands([]); }
  };
  const fetchProducts = async () => {
    try {
      const p = new URLSearchParams();
      if (category) p.append('category', category);
      if (brandFilter) p.append('brand', brandFilter);
      if (productType) p.append('product_type', productType);
      if (priceRange) { const [min, max] = priceRange.split('-'); if (min) p.append('price_min', min); if (max) p.append('price_max', max); }
      const r = await authFetch(`${API}/products?${p}`);
      if (r.ok) setAvailableProducts(await r.json());
    } catch (e) { /* */ }
  };

  // ── Template helpers ──
  const selectTemplate = (t) => {
    setActiveTemplate(t);
    setName(t.name);
    setCategory(t.category);
    setTemplateSlots(t.steps.map(() => ({ product: null })));
  };
  const clearTemplate = () => { setActiveTemplate(null); setTemplateSlots([]); setName(''); };
  const openPicker = (i) => { setPickerStepIndex(i); setPickerOpen(true); };
  const handlePickerSelect = (product) => {
    setTemplateSlots(prev => { const n = [...prev]; n[pickerStepIndex] = { product }; return n; });
  };
  const filledCount = templateSlots.filter(s => s.product).length;

  // ── Submit ──
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    const finalProducts = tab === 'routine' && activeTemplate
      ? templateSlots.filter(s => s.product).map(s => s.product.product_id)
      : selectedProducts;
    const finalName = name.trim();
    if (!finalName) { toast.error('Enter a checklist name'); return; }
    if (finalProducts.length < 3) { toast.error('Select at least 3 products'); return; }
    if (finalProducts.length > 7) { toast.error('Maximum 7 products'); return; }

    setLoading(true);
    try {
      const r = await authFetch(`${API}/checklists`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: finalName, filters: { category }, products: finalProducts })
      });
      if (r.ok) {
        const result = await r.json();
        toast.success('Checklist created!');
        setCreatedChecklistId(result.checklist_id || result.id);
        setShowRoutinePrompt(true);
      } else {
        const err = await r.json();
        throw new Error(err.detail || 'Failed');
      }
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  const handleTrackAsRoutine = async (schedule) => {
    if (!createdChecklistId) return;
    try {
      const r = await authFetch(`${API}/routines`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ checklist_id: createdChecklistId, schedule }),
      });
      if (r.ok) { toast.success('Routine created! Start checking in daily.'); navigate('/routines'); }
      else { navigate('/checklists'); }
    } catch (e) { navigate('/checklists'); }
  };
  const skipRoutine = () => { setShowRoutinePrompt(false); navigate('/checklists'); };

  const toggleProduct = (id) => setSelectedProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const moveProduct = (i, d) => setSelectedProducts(prev => { const a = [...prev]; const n = i + d; if (n < 0 || n >= a.length) return a; [a[i], a[n]] = [a[n], a[i]]; return a; });
  const getProductById = (id) => availableProducts.find(p => p.product_id === id);

  const filteredBySearch = availableProducts.filter(p => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return p.name.toLowerCase().includes(q) || p.brand.toLowerCase().includes(q);
  });
  const groupedProducts = filteredBySearch.reduce((g, p) => { const t = p.product_type || 'other'; if (!g[t]) g[t] = []; g[t].push(p); return g; }, {});

  const canSubmitScratch = selectedProducts.length >= 3 && name.trim();
  const canSubmitRoutine = activeTemplate && filledCount >= 3 && name.trim();

  return (
    <div className="min-h-screen pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button data-testid="back-btn" onClick={() => activeTemplate ? clearTemplate() : navigate(-1)} className="p-2 hover:bg-primary/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Create Checklist</h1>
          <button
            data-testid="checklist-submit-btn"
            onClick={handleSubmit}
            disabled={loading || (tab === 'scratch' ? !canSubmitScratch : !canSubmitRoutine)}
            className="btn-primary px-5 py-2 text-sm disabled:opacity-40"
          >
            {loading ? 'Saving...' : 'Create'}
          </button>
        </div>

        {/* Tabs — only show when no active template */}
        {!activeTemplate && (
          <div className="max-w-2xl mx-auto px-4 flex gap-1 pb-3">
            <button
              data-testid="tab-scratch"
              onClick={() => setTab('scratch')}
              className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
                tab === 'scratch' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-muted-foreground'
              }`}
            >From Scratch</button>
            <button
              data-testid="tab-routine"
              onClick={() => setTab('routine')}
              className={`flex-1 py-2 rounded-xl text-sm font-bold transition-all ${
                tab === 'routine' ? 'bg-primary text-white shadow-md' : 'bg-gray-100 text-muted-foreground'
              }`}
            >Quick Routine</button>
          </div>
        )}
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {user && user.checklist_count >= checklistMax && (
          <div className="mb-5 p-3 bg-destructive/10 border border-destructive/20 rounded-xl">
            <p className="text-destructive text-sm font-medium">Limit of {checklistMax} checklists reached.</p>
          </div>
        )}

        {/* ════════════════════════════════════ */}
        {/* TAB: QUICK ROUTINE                   */}
        {/* ════════════════════════════════════ */}
        {tab === 'routine' && !activeTemplate && (
          <div className="space-y-4" data-testid="routine-templates-section">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Pick a Routine</h3>
            </div>
            <p className="text-sm text-muted-foreground -mt-2">Choose a template, plug in your favourite products.</p>

            <div className="grid grid-cols-2 gap-3">
              {ROUTINE_TEMPLATES.map(t => (
                <button
                  key={t.id}
                  data-testid={`template-${t.id}`}
                  onClick={() => selectTemplate(t)}
                  className="text-left rounded-2xl overflow-hidden border border-border/50 shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98] bg-white"
                >
                  <div className={`bg-gradient-to-br ${t.color} p-3 pb-2`}>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-white/80 bg-white/20 px-2 py-0.5 rounded-full">{t.tag}</span>
                  </div>
                  <div className="p-3 pt-2">
                    <h4 className="font-bold text-sm leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{t.name}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">{t.steps.length} steps</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ════════════════════════════════════ */}
        {/* ACTIVE TEMPLATE: Fill products       */}
        {/* ════════════════════════════════════ */}
        {tab === 'routine' && activeTemplate && (
          <div className="space-y-5">
            {/* Template header */}
            <div className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${activeTemplate.color} p-4 text-white`}>
              <div className="absolute top-0 right-0 w-28 h-28 bg-white/10 rounded-full -translate-y-8 translate-x-8" />
              <div className="relative">
                <span className="text-[10px] font-bold uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">{activeTemplate.tag}</span>
                <h2 className="text-xl font-bold mt-1.5" style={{ fontFamily: 'Outfit, sans-serif' }}>{activeTemplate.name}</h2>
                <p className="text-xs opacity-80 mt-0.5">{activeTemplate.steps.length} steps &middot; Tap each to pick a product</p>
              </div>
            </div>

            {/* Name input */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Checklist Name</label>
              <input
                data-testid="checklist-name-input"
                type="text" value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={activeTemplate.name}
                className="w-full px-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none"
              />
            </div>

            {/* Steps */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Routine ({filledCount}/{activeTemplate.steps.length})</h3>
                <span className="text-[11px] text-muted-foreground">min 3</span>
              </div>

              {activeTemplate.steps.map((step, idx) => {
                const filled = templateSlots[idx]?.product;
                return (
                  <button
                    key={idx}
                    data-testid={`template-step-${idx}`}
                    onClick={() => openPicker(idx)}
                    className={`w-full text-left p-3.5 rounded-xl border-2 transition-all active:scale-[0.98] ${
                      filled ? 'border-primary/30 bg-primary/5' : 'border-dashed border-border hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                        filled ? 'bg-primary text-white' : 'bg-gray-200 text-muted-foreground'
                      }`}>
                        {filled ? <Check className="w-3.5 h-3.5" /> : idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        {filled ? (
                          <>
                            <p className="font-semibold text-sm truncate">{filled.name}</p>
                            <p className="text-xs text-muted-foreground">{filled.brand} &middot; {filled.price}</p>
                          </>
                        ) : (
                          <>
                            <p className="font-semibold text-sm">{step.label}</p>
                            <p className="text-xs text-muted-foreground">{step.hint}</p>
                          </>
                        )}
                      </div>
                      <ChevronRight className={`w-4 h-4 shrink-0 ${filled ? 'text-primary' : 'text-muted-foreground/30'}`} />
                    </div>
                  </button>
                );
              })}
            </div>

            <button onClick={clearTemplate} className="w-full text-center text-sm text-muted-foreground hover:text-primary py-1" data-testid="switch-to-templates">
              Choose a different template
            </button>
          </div>
        )}

        {/* ════════════════════════════════════ */}
        {/* TAB: FROM SCRATCH                    */}
        {/* ════════════════════════════════════ */}
        {tab === 'scratch' && (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-medium mb-1.5">Checklist Name</label>
              <input id="name" data-testid="checklist-name-input" type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="My Morning Routine"
                className="w-full px-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none placeholder:text-muted-foreground/40 placeholder:italic"
              />
            </div>

            {/* Filters */}
            <div className="space-y-3">
              <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</h3>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
                <select data-testid="category-select" value={category}
                  onChange={(e) => { setCategory(e.target.value); setProductType(''); setBrandFilter(''); setPriceRange(''); }}
                  className="w-full px-4 py-3 rounded-xl border-2 border-border bg-white/50 focus:border-primary outline-none"
                >
                  <option value="skincare">Face Care</option>
                  <option value="makeup">Make Up</option>
                  <option value="haircare">Hair Care</option>
                  <option value="pharmacy">Pharma</option>
                </select>
              </div>

              <div className="grid grid-cols-3 gap-2">
                {/* Brand */}
                <div className="relative" ref={brandDropdownRef}>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Brand</label>
                  <input data-testid="brand-filter" type="text"
                    value={brandFilter || brandSearch}
                    onChange={(e) => { if (brandFilter) setBrandFilter(''); handleBrandSearch(e.target.value); setShowBrandDropdown(true); }}
                    onFocus={() => setShowBrandDropdown(true)}
                    placeholder="Search..."
                    className="w-full px-2 py-2 rounded-lg border border-border bg-white/50 focus:border-primary outline-none text-xs"
                  />
                  {brandFilter && <button onClick={() => { setBrandFilter(''); setBrandSearch(''); }} className="absolute right-1.5 top-7 text-muted-foreground text-xs">&times;</button>}
                  {showBrandDropdown && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-border rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      <button onClick={() => { setBrandFilter(''); setBrandSearch(''); setShowBrandDropdown(false); }} className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 text-muted-foreground">All Brands</button>
                      {brandSearch.length >= 1 ? (
                        searchedBrands.length > 0 ? searchedBrands.map(b => (
                          <button key={b.brand} onClick={() => { setBrandFilter(b.brand); setBrandSearch(''); setShowBrandDropdown(false); }}
                            className="w-full text-left px-2 py-1.5 text-xs hover:bg-primary/5 flex justify-between">
                            <span className="truncate">{b.brand}</span><span className="text-muted-foreground shrink-0 ml-1">{b.count}</span>
                          </button>
                        )) : <p className="px-2 py-1.5 text-[10px] text-muted-foreground">No brands found</p>
                      ) : <p className="px-2 py-1.5 text-[10px] text-muted-foreground">Type to search...</p>}
                    </div>
                  )}
                </div>
                {/* Type */}
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Type</label>
                  <select data-testid="type-filter" value={productType} onChange={(e) => setProductType(e.target.value)}
                    className="w-full px-2 py-2 rounded-lg border border-border bg-white/50 focus:border-primary outline-none text-xs">
                    <option value="">All</option>
                    {typesList.map(t => <option key={t} value={t}>{formatTypeName(t)}</option>)}
                  </select>
                </div>
                {/* Price */}
                <div>
                  <label className="block text-[10px] font-medium text-muted-foreground mb-0.5">Price</label>
                  <select data-testid="price-filter" value={priceRange} onChange={(e) => setPriceRange(e.target.value)}
                    className="w-full px-2 py-2 rounded-lg border border-border bg-white/50 focus:border-primary outline-none text-xs">
                    <option value="">Any</option>
                    <option value="0-300">Under ₹300</option>
                    <option value="300-500">₹300-500</option>
                    <option value="500-1000">₹500-1k</option>
                    <option value="1000-2000">₹1k-2k</option>
                    <option value="2000-">₹2k+</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Selected Products */}
            {selectedProducts.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Order ({selectedProducts.length}/7)</h3>
                <div className="space-y-1.5 p-2.5 bg-primary/5 rounded-xl border border-primary/20">
                  {selectedProducts.map((id, i) => {
                    const p = getProductById(id);
                    if (!p) return null;
                    return (
                      <div key={id} className="flex items-center gap-2 bg-white rounded-lg p-2 shadow-sm" data-testid={`selected-product-${i}`}>
                        <GripVertical className="w-3.5 h-3.5 text-muted-foreground/30 shrink-0" />
                        <span className="w-5 h-5 rounded-full bg-primary text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold truncate">{p.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate">{p.brand}</p>
                        </div>
                        <div className="flex gap-0.5 shrink-0">
                          <button type="button" onClick={() => moveProduct(i, -1)} disabled={i === 0} className="p-0.5 disabled:opacity-20"><ChevronUp className="w-3.5 h-3.5" /></button>
                          <button type="button" onClick={() => moveProduct(i, 1)} disabled={i === selectedProducts.length - 1} className="p-0.5 disabled:opacity-20"><ChevronDown className="w-3.5 h-3.5" /></button>
                        </div>
                        <button type="button" onClick={() => toggleProduct(id)} className="p-1 text-muted-foreground hover:text-red-500"><Minus className="w-3.5 h-3.5" /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Product Selection */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Products ({selectedProducts.length}/7)</h3>
                <span className="text-[10px] text-muted-foreground bg-white/70 px-2 py-0.5 rounded-full">Pick 3-7</span>
              </div>

              <div className="relative">
                <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="text" data-testid="product-search" value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full px-4 py-2.5 pl-9 rounded-xl border border-border bg-white/50 focus:border-primary focus:ring-2 focus:ring-primary/10 outline-none text-sm"
                />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-muted-foreground" /></button>}
              </div>

              {availableProducts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">No products match filters</div>
              ) : (
                <div className="space-y-5">
                  {Object.entries(groupedProducts).map(([type, prods]) => (
                    <div key={type}>
                      <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wide mb-2 pl-1">{formatTypeName(type)}</h4>
                      <div className="space-y-2">
                        {prods.map(p => (
                          <button key={p.product_id} data-testid={`product-${p.product_id}`} type="button"
                            onClick={() => toggleProduct(p.product_id)}
                            className={`w-full p-3 rounded-xl border-2 transition-all text-left active:scale-[0.98] ${
                              selectedProducts.includes(p.product_id) ? 'border-primary bg-primary/5' : 'border-border bg-white/50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              {p.image ? (
                                <img src={p.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = 'flex'; }} />
                              ) : null}
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-pink-400 to-orange-300 items-center justify-center text-white font-bold shrink-0"
                                style={{ fontFamily: 'Outfit, sans-serif', display: p.image ? 'none' : 'flex' }}>g</div>
                              <div className="flex-1 min-w-0">
                                <p className="font-bold text-sm leading-tight line-clamp-2">{p.name}</p>
                                <p className="text-xs text-muted-foreground">{p.brand}</p>
                                <p className="text-xs font-medium text-primary">{p.price}</p>
                              </div>
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                selectedProducts.includes(p.product_id) ? 'border-primary bg-primary' : 'border-border'
                              }`}>
                                {selectedProducts.includes(p.product_id) && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        )}
      </div>

      {/* Full-screen product picker (routine mode) */}
      {activeTemplate && pickerStepIndex !== null && (
        <ProductPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={handlePickerSelect}
          step={activeTemplate.steps[pickerStepIndex]}
          category={activeTemplate.category}
          currentProductId={templateSlots[pickerStepIndex]?.product?.product_id}
        />
      )}

      {/* Track as Routine prompt */}
      {showRoutinePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="routine-prompt-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={skipRoutine} />
          <div className="relative bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-2xl animate-in zoom-in-95 duration-300">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-orange-400 to-rose-400 rounded-full flex items-center justify-center">
                <Flame className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Track as Daily Routine?</h3>
              <p className="text-sm text-muted-foreground">Check in daily, build streaks, and earn badges.</p>
              <div className="grid grid-cols-3 gap-2 pt-2">
                <button data-testid="track-morning" onClick={() => handleTrackAsRoutine('morning')}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-amber-400 hover:bg-amber-50 transition-all">
                  <Sun className="w-6 h-6 text-amber-500" /><span className="text-xs font-bold">Morning</span>
                </button>
                <button data-testid="track-night" onClick={() => handleTrackAsRoutine('night')}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-indigo-400 hover:bg-indigo-50 transition-all">
                  <Moon className="w-6 h-6 text-indigo-500" /><span className="text-xs font-bold">Night</span>
                </button>
                <button data-testid="track-both" onClick={() => handleTrackAsRoutine('both')}
                  className="flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 border-border hover:border-primary hover:bg-primary/5 transition-all">
                  <Clock className="w-6 h-6 text-primary" /><span className="text-xs font-bold">Both</span>
                </button>
              </div>
              <button data-testid="skip-routine" onClick={skipRoutine} className="text-sm text-muted-foreground hover:text-foreground">Skip for now</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
